import { Object3D, Vector3, Group } from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

import { Context } from "../../context";
import { calculateAlpha } from "../../util/clock";
import { Frames } from "../../util/frames";

import { BallTrail } from "./trail";
import { BallPad } from "./pad";

const SPIN_DAMPING = 1 / 4;

export class Ball extends Object3D {
  ball: Group;
  trail: BallTrail;
  pad: BallPad;

  spinAxis: Vector3 = new Vector3();
  spinRate: number = 0;

  age?: number;
  contextRef: Context;

  prevTime?: number;
  prevFrameNum?: number;

  constructor(context: Context) {
    super();

    this.ball = new Group();
    this.pad = new BallPad(context);
    this.trail = new BallTrail();

    const loader = new GLTFLoader().setPath(`${context.options.assetBase}`);
    loader.load("/models/rbi-ball.glb", (model) => {
      this.ball.scale.setScalar(context.state.ballScale);
      this.ball.add(model.scene);
      this.ball.visible = false;
    });

    this.add(this.ball);
    this.add(this.pad);
    this.add(this.trail);

    this.contextRef = context;
  }

  update(frames: Frames, time: number) {
    const { state } = this.contextRef;

    if (frames.current && frames.current.ballPosition) {
      this.ball.visible = state.ballEnabled;

      if (this.prevFrameNum != frames.current.num) {
        this.age = 0;
      } else {
        if (this.age != undefined) this.age++;
      }

      const neighbors = frames.getNeighbours<[number, number, number]>(
        (frame) => {
          return frame.ballPosition as [number, number, number] | undefined;
        }
      );

      if (
        neighbors &&
        neighbors.prev?.time != undefined &&
        neighbors.next?.time != undefined
      ) {
        const alpha = calculateAlpha(
          [neighbors.prev.time, neighbors.next.time],
          time
        );
        if (alpha != undefined) {
          const ballPosition = new Vector3(
            neighbors.prev.neighbor[0] * (1 - alpha) +
              alpha * neighbors.next.neighbor[0],
            neighbors.prev.neighbor[1] * (1 - alpha) +
              alpha * neighbors.next.neighbor[1],
            neighbors.prev.neighbor[2] * (1 - alpha) +
              alpha * neighbors.next.neighbor[2]
          );

          this.ball.position.copy(ballPosition);
          this.pad.update(ballPosition);
        }
      }

      //probably not true to life
      if (frames.current.ballPolynomials) {
        for (let poly of frames.current.ballPolynomials) {
          if (poly.packet_type == "pitch_refined") {
            this.spinAxis.set(poly.x, poly.y, poly.z).normalize();
            this.spinRate = poly.spin_rate;
          }
        }
      }
      let delta =
        2 *
        Math.PI *
        (this.spinRate / 60) *
        (time - (this.prevTime ?? time)) *
        SPIN_DAMPING;
      this.ball.rotateOnAxis(this.spinAxis, delta);
    } else {
      this.ball.visible = false;
      this.pad.visible = false;
    }

    if (this.prevFrameNum != frames.current?.num) {
      this.trail.visible = state.trailEnabled;
      this.trail.update(frames);
    }

    this.prevFrameNum = frames.current?.num;
    this.prevTime = time;
  }
}
